import React from 'react';
import { Form, Input, Button, Typography, App } from 'antd';
import {
  MailOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { forgotPassword } from '../../store/slices/authSlice';
import { AuthSplitLayout } from './AuthSplitLayout';
import { AuthFormCard } from './AuthFormCard';
import {
  AuthHeroBadge,
  AuthHeroTitle,
  AuthHeroDescription,
  AuthHeroHighlights,
  AuthHeroFeatureHeading,
  AuthHeroFeatureList,
} from './authHeroPrimitives';
const { Text } = Typography;

const ForgotPassword: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state) => state.auth);
  const { message } = App.useApp();

  const onFinish = async (values: { email: string }) => {
    try {
      await dispatch(forgotPassword(values.email)).unwrap();
      message.success('Password reset email sent! Please check your inbox.');
      form.resetFields();
    } catch (err) {
      message.error(err as string);
    }
  };

  const highlights = [
    { value: 'Email only', label: 'No password yet' },
    { value: 'Time-limited', label: 'Secure link' },
    { value: 'Your inbox', label: 'Check spam too' },
  ];

  const features = [
    {
      icon: <SendOutlined />,
      title: 'Use your vendor email',
      description: 'We only send reset mail to the address tied to your TOC account.',
    },
    {
      icon: <LinkOutlined />,
      title: 'One tap from the email',
      description: 'Open the link on this device or phone—same secure flow.',
    },
    {
      icon: <ClockCircleOutlined />,
      title: 'Link expires',
      description: 'If it lapses, request a fresh reset from this page anytime.',
    },
  ];

  const hero = (
    <>
      <AuthHeroBadge icon={<MailOutlined />}>Account access</AuthHeroBadge>
      <AuthHeroTitle>Reset your password safely</AuthHeroTitle>
      <AuthHeroDescription>
        Locked out? We will email you a single-use link so you can set a new password. For your protection we never show
        whether an email exists in our system—check the inbox for the address you use to sign in.
      </AuthHeroDescription>
      <AuthHeroHighlights items={highlights} />
      <AuthHeroFeatureHeading>How it works</AuthHeroFeatureHeading>
      <AuthHeroFeatureList items={features} />
    </>
  );

  const leftFooter = (
    <>
      <span className="inline-flex items-center gap-1">
        <CheckCircleOutlined className="text-[#4ADE80]" />
        Same security as sign-in
      </span>
      <span className="text-white/50">·</span>
      <span className="inline-flex items-center gap-1 text-white/70">
        <SafetyCertificateOutlined className="text-xs text-[#F7941F]" />
        Vendor accounts only
      </span>
    </>
  );

  return (
    <AuthSplitLayout hero={hero} footer={leftFooter}>
      <AuthFormCard
        title="Forgot password"
        subtitle="Enter the email you use for The Old Cart vendor sign-in. We will send reset instructions if we find a matching account."
      >
        <Form
          form={form}
          name="forgotPassword"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          requiredMark={false}
          className="auth-form-fields-tight"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Enter your email' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-[#0171BF]" />}
              placeholder="Email address on your account"
              autoComplete="email"
              aria-label="Email address on your account"
              className="!min-h-[2.75rem] rounded-lg border-slate-200 text-[15px] transition-colors hover:border-[#0171BF]/40 focus:border-[#0171BF]"
            />
          </Form.Item>

          {error && (
            <div className="mb-3 rounded-lg border border-red-200/80 bg-red-50/90 px-3.5 py-2.5" role="alert">
              <Text className="text-sm text-red-700">{error}</Text>
            </div>
          )}

          <Form.Item className="auth-form-item--compact">
            <Button type="primary" htmlType="submit" loading={loading} block size="large" className="!h-12 !rounded-lg !font-semibold">
              Send reset link
            </Button>
          </Form.Item>
        </Form>

        <p className="mb-0 mt-4 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0171BF] hover:text-[#015a9e] hover:underline"
          >
            <ArrowLeftOutlined />
            Back to sign in
          </Link>
        </p>

        <p className="mt-6 text-center text-xs leading-relaxed text-slate-500 sm:text-[13px]">
          Requesting a reset means you agree we may email you transactionally about your vendor account. See our{' '}
          <Link to="/privacy" className="font-medium text-[#0171BF] hover:underline">
            Privacy Policy
          </Link>{' '}
          for how we handle contact data.
        </p>
      </AuthFormCard>
    </AuthSplitLayout>
  );
};

export default ForgotPassword;
